import { useContext, useMemo } from 'react';
import { Alert, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { Page, Row } from '../../components';
import { SCHOOLS, SPELLS } from '../../data';
import { AuthContext, FilterContext, SettingsContext } from '../../context';
import { applyAllFilters } from '../../util/spell';
import '../SpellsReferencePage/SpellsReferencePage.scss';

const CHART_COLORS = ['#7c5cff', '#9b7dff', '#c4a5ff', '#4ecdc4', '#ffb86f', '#ff7aa2', '#6ac5ff'];

const getSpellClasses = (spell) => {
    if (!spell?.classes) return [];
    if (Array.isArray(spell.classes)) return spell.classes;
    return spell.classes.fromClassList?.map(({ name }) => name) || [];
};

const formatSpellRange = (spell) => {
    if (!spell?.range) return 'Range';
    if (typeof spell.range === 'string') return spell.range;
    if (spell.range.distance?.type === 'touch') return 'Touch';
    if (spell.range.distance?.type === 'self') return 'Self';
    if (spell.range.distance?.amount) return `${spell.range.distance.amount} ft`;
    return spell.range.type || 'Range';
};

const buildChartGradient = (entries) => {
    const total = entries.reduce((sum, item) => sum + item.value, 0) || 1;
    let start = 0;

    return entries.map(({ color, value }) => {
        const end = start + (value / total) * 100;
        const segment = `${color} ${start}% ${end}%`;
        start = end;
        return segment;
    }).join(', ');
};

export const InsightsPage = () => {
    const { isLoggedIn } = useContext(AuthContext);
    const context = useContext(FilterContext) || {
        searchString: '',
        setSearchString: () => {},
        filters: {},
        setFilters: () => {},
    };
    const { searchString, filters } = context;
    const settings = useContext(SettingsContext);

    const compareSpells = (s1, s2) => {
        if (s1.level === s2.level) {
            return s1.name.localeCompare(s2.name);
        }

        return s1.level - s2.level;
    };

    const visibleSpells = useMemo(() => {
        const regex = new RegExp(searchString.toLowerCase());

        return SPELLS
            .filter(({ name }) => regex.test(name.toLowerCase()))
            .filter(s => applyAllFilters(s, filters, settings))
            .sort(compareSpells);
    }, [searchString, filters, settings]);

    const stats = useMemo(() => {
        const total = visibleSpells.length || 1;
        const ritualCount = visibleSpells.filter(spell => !!spell?.meta?.ritual).length;
        const concentrationCount = visibleSpells.filter(spell => spell?.duration?.some(d => d.concentration)).length;

        const schoolCounts = Object.keys(SCHOOLS).reduce((acc, key) => {
            acc[key] = 0;
            return acc;
        }, {});

        visibleSpells.forEach(spell => {
            if (schoolCounts[spell.school] !== undefined) {
                schoolCounts[spell.school] += 1;
            }
        });

        const levelCounts = Array.from({ length: 10 }, (_, level) => ({ level, count: visibleSpells.filter(s => s.level === level).length }));

        const damageCounts = {};
        visibleSpells.forEach(spell => {
            (spell.damageInflict || []).forEach(damage => {
                damageCounts[damage] = (damageCounts[damage] || 0) + 1;
            });
        });

        const castingCounts = {
            action: 0,
            bonus: 0,
            reaction: 0,
            other: 0,
        };

        visibleSpells.forEach(spell => {
            const time = spell.time?.[0];
            if (!time) return;

            if (['action', 'bonus', 'reaction'].includes(time.unit.toLowerCase())) {
                castingCounts[time.unit.toLowerCase()] += 1;
            } else {
                castingCounts.other += 1;
            }
        });

        const schoolEntries = Object.entries(schoolCounts)
            .map(([key, value]) => ({ label: SCHOOLS[key], value, color: CHART_COLORS[Object.keys(schoolCounts).indexOf(key) % CHART_COLORS.length] }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);

        const damageEntries = Object.entries(damageCounts)
            .map(([label, value]) => ({ label, value, color: CHART_COLORS[Object.keys(damageCounts).indexOf(label) % CHART_COLORS.length] }))
            .sort((a, b) => b.value - a.value);

        const castingEntries = Object.entries(castingCounts)
            .map(([label, value]) => ({ label: label.toUpperCase(), value, color: CHART_COLORS[Object.keys(castingCounts).indexOf(label) % CHART_COLORS.length] }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);

        const ritualSchoolCounts = Object.keys(SCHOOLS).reduce((acc, key) => {
            acc[key] = 0;
            return acc;
        }, {});
        const concentrationSchoolCounts = Object.keys(SCHOOLS).reduce((acc, key) => {
            acc[key] = 0;
            return acc;
        }, {});

        visibleSpells.forEach(spell => {
            if (spell?.meta?.ritual && ritualSchoolCounts[spell.school] !== undefined) {
                ritualSchoolCounts[spell.school] += 1;
            }
            if (spell?.duration?.some(d => d.concentration) && concentrationSchoolCounts[spell.school] !== undefined) {
                concentrationSchoolCounts[spell.school] += 1;
            }
        });

        const ritualSchoolEntries = Object.entries(ritualSchoolCounts)
            .map(([key, value]) => ({ label: SCHOOLS[key], value, color: CHART_COLORS[Object.keys(ritualSchoolCounts).indexOf(key) % CHART_COLORS.length] }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);

        const concentrationSchoolEntries = Object.entries(concentrationSchoolCounts)
            .map(([key, value]) => ({ label: SCHOOLS[key], value, color: CHART_COLORS[Object.keys(concentrationSchoolCounts).indexOf(key) % CHART_COLORS.length] }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);

        return {
            total,
            ritualRate: (ritualCount / total) * 100,
            concentrationRate: (concentrationCount / total) * 100,
            schoolEntries,
            damageEntries,
            castingEntries,
            ritualSchoolEntries,
            concentrationSchoolEntries,
            levelCounts,
            schoolGradient: buildChartGradient(schoolEntries),
            damageGradient: buildChartGradient(damageEntries),
            castingGradient: buildChartGradient(castingEntries),
        };
    }, [visibleSpells]);

    const classCounts = useMemo(() => {
        const counts = {};

        visibleSpells.forEach(spell => {
            const classes = getSpellClasses(spell);
            if (!classes.length) {
                counts.General = (counts.General || 0) + 1;
                return;
            }

            classes.forEach(cls => {
                counts[cls] = (counts[cls] || 0) + 1;
            });
        });

        return Object.entries(counts)
            .map(([label, value]) => ({ label, value }))
            .sort((a, b) => b.value - a.value);
    }, [visibleSpells]);

    const rangeCounts = useMemo(() => {
        const counts = {};

        visibleSpells.forEach(spell => {
            const rangeLabel = formatSpellRange(spell);
            counts[rangeLabel] = (counts[rangeLabel] || 0) + 1;
        });

        return Object.entries(counts)
            .map(([label, value]) => ({ label, value }))
            .sort((a, b) => b.value - a.value);
    }, [visibleSpells]);

    return (
        <Page className="SpellsReferencePage">
            {!isLoggedIn &&
                <Alert severity='info'>
                    <Link to="/emaillinksignin">Save your spell lists</Link> to keep your favorites synced and ready when you return.
                </Alert>
            }
            <Row className='headingRow'>
                <h2>Spell Insights</h2>
            </Row>
            <div className="InsightsView">
                <div className="insightHeader">
                    <div>
                        <div className="eyebrow">Dataset explorer</div>
                        <h3>Spell trends</h3>
                    </div>
                    <Button component={Link} to="/" variant="outlined" color="secondary">
                        Back to all spells
                    </Button>
                </div>

                <div className="statsGrid">
                    <div className="statCard">
                        <div className="statLabel">Ritual rate</div>
                        <div className="statValue">{Math.round(stats.ritualRate)}%</div>
                        <div className="progressTrack">
                            <div className="progressFill ritual" style={{ width: `${stats.ritualRate}%` }} />
                        </div>
                        <div className="statMeta">
                            {Math.round((stats.ritualRate / 100) * stats.total)} of {stats.total} spells
                        </div>
                    </div>
                    <div className="statCard">
                        <div className="statLabel">Concentration</div>
                        <div className="statValue">{Math.round(stats.concentrationRate)}%</div>
                        <div className="progressTrack">
                            <div className="progressFill concentration" style={{ width: `${stats.concentrationRate}%` }} />
                        </div>
                        <div className="statMeta">
                            {Math.round((stats.concentrationRate / 100) * stats.total)} of {stats.total} spells
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">School mix</span>
                            <span className="cardCount">{stats.total} spells</span>
                        </div>
                        <div className="donutWrap">
                            <div className="donutChart" style={{ background: `conic-gradient(${stats.schoolGradient})` }}>
                                <div className="donutInner">
                                    <strong>{stats.total}</strong>
                                </div>
                            </div>
                            <ul className="legendList">
                                {stats.schoolEntries.map(item => (
                                    <li key={item.label}>
                                        <span className="legendSwatch" style={{ background: item.color }} />
                                        <span>{item.label}</span>
                                        <strong>{item.value}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Damage types</span>
                            <span className="cardCount">All</span>
                        </div>
                        <div className="donutWrap">
                            <div className="donutChart small" style={{ background: `conic-gradient(${stats.damageGradient})` }}>
                                <div className="donutInner">
                                    <strong>{stats.total}</strong>
                                </div>
                            </div>
                            <ul className="legendList">
                                {stats.damageEntries.map(item => (
                                    <li key={item.label}>
                                        <span className="legendSwatch" style={{ background: item.color }} />
                                        <span>{item.label}</span>
                                        <strong>{item.value}</strong>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Ritual by school</span>
                            <span className="cardCount">All</span>
                        </div>
                        <div className="barChart">
                            {stats.ritualSchoolEntries.map(item => (
                                <div key={item.label} className="barRow">
                                    <span>{item.label}</span>
                                    <div className="barTrack">
                                        <div className="barFill" style={{ width: `${(item.value / Math.max(stats.total, 1)) * 100}%`, background: item.color }} />
                                    </div>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Concentration by school</span>
                            <span className="cardCount">All</span>
                        </div>
                        <div className="barChart">
                            {stats.concentrationSchoolEntries.map(item => (
                                <div key={item.label} className="barRow">
                                    <span>{item.label}</span>
                                    <div className="barTrack">
                                        <div className="barFill" style={{ width: `${(item.value / Math.max(stats.total, 1)) * 100}%`, background: item.color }} />
                                    </div>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Spell level spread</span>
                            <span className="cardCount">Total</span>
                        </div>
                        <div className="levelChart">
                            {stats.levelCounts.map(({ level, count }) => (
                                <div key={level} className="levelRow">
                                    <span>{level === 0 ? 'Cantrip' : `Lvl ${level}`}</span>
                                    <div className="levelTrack">
                                        <div className="levelFill" style={{ width: `${(count / stats.total) * 100}%` }} />
                                    </div>
                                    <strong>{count}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Casting time</span>
                            <span className="cardCount">Breakdown</span>
                        </div>
                        <div className="barChart">
                            {stats.castingEntries.map(item => (
                                <div key={item.label} className="barRow">
                                    <span>{item.label}</span>
                                    <div className="barTrack">
                                        <div className="barFill" style={{ width: `${(item.value / stats.total) * 100}%`, background: item.color }} />
                                    </div>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Class mix</span>
                            <span className="cardCount">All</span>
                        </div>
                        <div className="barChart">
                            {classCounts.map(item => (
                                <div key={item.label} className="barRow">
                                    <span>{item.label}</span>
                                    <div className="barTrack">
                                        <div className="barFill" style={{ width: `${(item.value / stats.total) * 100}%`, background: CHART_COLORS[classCounts.indexOf(item) % CHART_COLORS.length] }} />
                                    </div>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="statCard fullWidth">
                        <div className="cardHeader">
                            <span className="statLabel">Range distribution</span>
                            <span className="cardCount">All</span>
                        </div>
                        <div className="barChart">
                            {rangeCounts.map(item => (
                                <div key={item.label} className="barRow">
                                    <span>{item.label}</span>
                                    <div className="barTrack">
                                        <div className="barFill" style={{ width: `${(item.value / stats.total) * 100}%`, background: CHART_COLORS[rangeCounts.indexOf(item) % CHART_COLORS.length] }} />
                                    </div>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};
